import { useState, useEffect, forwardRef } from "react";
import axios from "axios";

const UserForm = forwardRef(
  (
    {
      isEditing,
      user,
      setIsEditing,
      setIsAdding,
      isViewOnly,
      accounts,
      unions,
      setUsers,
      setStagedUsers,
      setDialog,
      laborRates = [],
    },
    ref,
  ) => {
    const [formData, setFormData] = useState({
      displayName: "",
      email: "",
      phoneNumber: "",
      paymentType: "contractor",
      employeeNumber: "",
      payRate: 0,
      payType: [],
      role: "unassigned",
      union: "non-union",
      level: "1",
      active: true,
      isTemporary: true,
      geoFence: false,
      wsib: true,
      account: null,
      hoursPerWeek: null,
      collectsTax: true,
      taxRate: 0,
      workerClassification: null,
    });

    const [isPayTypeOpen, setIsPayTypeOpen] = useState(false);

    const payTypeOptions = [
      { value: "pieceworker", label: "Pieceworker" },
      { value: "hourly", label: "Hourly" },
      { value: "subcontractor", label: "Subcontractor" },
      { value: "salary", label: "Salary" },
    ];

    const getPayTypeOptions = (isTemporary) =>
      isTemporary
        ? payTypeOptions.filter(
            (o) => o.value === "hourly" || o.value === "pieceworker",
          )
        : payTypeOptions;

    const isSiteOrForeman = (role) => ["site_worker", "foreman"].includes(role);

    useEffect(() => {
      if ((isEditing || isViewOnly) && user) {
        setFormData({
          ...user,
          payType: Array.isArray(user.payType)
            ? user.payType
            : user.payType
              ? [user.payType]
              : [],
          hoursPerWeek: user.hoursPerWeek || null,
          collectsTax: user.collectsTax || false,
          taxRate: user.taxRate || 0,
          wsib: user.wsib !== undefined ? user.wsib : true,
          phoneNumber: user.phoneNumber || "",
          paymentType: user.paymentType || "contractor",
          geoFence: user.geoFence || false,
          level: user.level || "1",
          account: user.account?._id || user.account || null,
          workerClassification: user.workerClassification || null,
        });
      }
    }, [isEditing, isViewOnly, user]);

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;

      setFormData((prev) => {
        let updated = {
          ...prev,
          [name]:
            type === "checkbox"
              ? checked
              : ["payRate", "hoursPerWeek", "taxRate"].includes(name)
                ? parseFloat(value) || 0
                : name === "workerClassification" && value === ""
                  ? null
                  : value,
        };

        // Auto-fill from Labor Rate Classifications
        if (name === "workerClassification" && value && laborRates.length > 0) {
          const rateEntry = laborRates.find(
            (r) => r.name?.trim().toLowerCase() === value.trim().toLowerCase(),
          );
          if (rateEntry) {
            if (rateEntry.hourlyRate > 0)
              updated.payRate = rateEntry.hourlyRate;
            if (rateEntry.isUnion && rateEntry.unionName) {
              updated.union = rateEntry.unionName;
              updated.level = rateEntry.unionLevel || "1";
            }
          }
        }

        // Clear classification fields if role changes away from site_worker/foreman
        if (name === "role" && !isSiteOrForeman(value)) {
          updated.workerClassification = null;
          updated.union = "non-union";
          updated.level = "1";
        }

        if (name === "role" && value !== "site_worker") {
          updated.paymentType = "contractor";
        }

        return updated;
      });
    };

    const handleToggle = (field) => {
      setFormData((prev) => {
        const updated = { ...prev, [field]: !prev[field] };
        if (field === "isTemporary" && updated.isTemporary) {
          updated.payType = updated.payType.filter(
            (t) => t !== "subcontractor" && t !== "salary",
          );
        }
        if (field === "isTemporary" && !updated.isTemporary) {
          updated.workerClassification = null;
        }
        return updated;
      });
    };

    const handlePayTypeToggle = (value) => {
      setFormData((prev) => {
        if (prev.isTemporary && value === "subcontractor") {
          setDialog({
            open: true,
            type: "error",
            title: "Invalid Selection",
            message: "Temporary users cannot be set as subcontractors.",
            onConfirm: () => setDialog((d) => ({ ...d, open: false })),
          });
          return prev;
        }
        const newPayTypes = prev.payType.includes(value)
          ? prev.payType.filter((t) => t !== value)
          : [...prev.payType, value];
        return {
          ...prev,
          payType: newPayTypes,
          account: newPayTypes.includes("subcontractor") ? prev.account : null,
          collectsTax: newPayTypes.includes("pieceworker")
            ? prev.collectsTax
            : false,
          taxRate: newPayTypes.includes("pieceworker") ? prev.taxRate : 0,
          hoursPerWeek:
            newPayTypes.includes("hourly") || newPayTypes.includes("salary")
              ? prev.hoursPerWeek || 40
              : null,
        };
      });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        const token = localStorage.getItem("token");
        const url = isEditing
          ? `https://erp-production-72da01c8e651.herokuapp.com/api/users/${user._id}`
          : `https://erp-production-72da01c8e651.herokuapp.com/api/users`;
        const method = isEditing ? "put" : "post";

        const response = await axios[method](url, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (isEditing) {
          setUsers((prev) =>
            prev.map((u) => (u._id === user._id ? response.data.user : u)),
          );
          setStagedUsers((prev) =>
            prev.map((u) => (u._id === user._id ? response.data.user : u)),
          );
        } else {
          setUsers((prev) =>
            [...prev, response.data.user].sort((a, b) =>
              a.displayName.localeCompare(b.displayName),
            ),
          );
          setStagedUsers((prev) =>
            [...prev, response.data.user].sort((a, b) =>
              a.displayName.localeCompare(b.displayName),
            ),
          );
        }

        setDialog({
          open: true,
          type: "success",
          title: "Success",
          message: isEditing
            ? "User updated successfully!"
            : "User added successfully!",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });

        setIsEditing(false);
        setIsAdding(false);
      } catch (err) {
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message:
            err.response?.data?.message ||
            `Failed to ${isEditing ? "update" : "add"} user.`,
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
      }
    };

    const handleCancel = () => {
      setIsEditing(false);
      setIsAdding(false);
    };

    const unionOptions = [
      { value: "non-union", label: "Non-Union" },
      ...(unions || []).map((u) => ({ value: u.title, label: u.title })),
    ];

    const isSiteOrForemanRole = isSiteOrForeman(formData.role);
    const showClassification = isSiteOrForemanRole;
    const showUnion = isSiteOrForemanRole;
    const showLevel =
      showUnion && formData.union && formData.union !== "non-union";

    const showHoursPerWeek = formData.payType.includes("salary");
    const showAccount = formData.payType.includes("subcontractor");
    const showPieceworker = formData.payType.includes("pieceworker");

    const inputClass =
      "mt-1 block w-full rounded-md bg-gray-700 border border-gray-600 text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors";
    const labelClass =
      "block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1";

    const Toggle = ({ field, label }) => (
      <label className="flex items-center justify-between cursor-pointer group">
        <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
          {label}
        </span>
        <button
          type="button"
          onClick={() => !isViewOnly && handleToggle(field)}
          disabled={isViewOnly}
          className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${
            formData[field] ? "bg-orange-400" : "bg-gray-600"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
              formData[field] ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </label>
    );

    const SectionHeader = ({ title }) => (
      <div className="col-span-2 flex items-center gap-3 pt-2">
        <span className="text-xs font-semibold text-orange-400 uppercase tracking-widest">
          {title}
        </span>
        <div className="flex-1 h-px bg-gray-700" />
      </div>
    );

    return (
      <div
        ref={ref}
        className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
      >
        <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl border border-gray-700 flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 shrink-0">
            <div>
              <h3 className="text-base font-semibold text-white">
                {isViewOnly
                  ? "User Details"
                  : isEditing
                    ? "Edit User"
                    : "New User"}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {isViewOnly
                  ? "Viewing user profile"
                  : isEditing
                    ? "Update user information"
                    : "Fill in the details to create a new crew member"}
              </p>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="text-gray-500 hover:text-gray-300 transition-colors p-1 rounded-md hover:bg-gray-700"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="overflow-y-auto flex-1">
            <div className="px-6 py-5 grid grid-cols-2 gap-x-5 gap-y-4">
              <SectionHeader title="Identity" />

              <div>
                <label className={labelClass}>Display Name</label>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  disabled={isViewOnly}
                  required
                  className={inputClass}
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isViewOnly}
                  required
                  className={inputClass}
                  placeholder="jane@company.com"
                />
              </div>
              <div>
                <label className={labelClass}>Phone Number</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  disabled={isViewOnly}
                  className={inputClass}
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div>
                <label className={labelClass}>Employee #</label>
                <input
                  type="text"
                  name="employeeNumber"
                  value={formData.employeeNumber}
                  onChange={handleChange}
                  disabled={isViewOnly}
                  className={inputClass}
                  placeholder="EMP-001"
                />
              </div>

              <SectionHeader title="Role & Classification" />

              <div>
                <label className={labelClass}>Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  disabled={isViewOnly}
                  className={inputClass}
                >
                  <option value="unassigned">Unassigned</option>
                  {formData.isTemporary ? (
                    <>
                      <option value="site_worker">Site Worker</option>
                      <option value="foreman">Foreman</option>
                    </>
                  ) : (
                    <>
                      <option value="admin">Admin</option>
                      <option value="foreman">Foreman</option>
                      <option value="project_manager">Project Manager</option>
                      <option value="site_worker">Site Worker</option>
                      <option value="estimator">Estimator</option>
                    </>
                  )}
                </select>
              </div>
              {formData.role === "site_worker" && (
                <div>
                  <label className={labelClass}>Payment Type</label>
                  <select
                    name="paymentType"
                    value={formData.paymentType || "contractor"}
                    onChange={handleChange}
                    disabled={isViewOnly}
                    className={inputClass}
                  >
                    <option value="contractor">Contractor</option>
                    <option value="hourly">Hourly</option>
                  </select>
                </div>
              )}

              {showClassification && (
                <div>
                  <label className={labelClass}>Worker Classification</label>
                  <select
                    name="workerClassification"
                    value={formData.workerClassification || ""}
                    onChange={handleChange}
                    disabled={isViewOnly}
                    className={inputClass}
                  >
                    <option value="">Select classification...</option>
                    {laborRates.map((rate) => (
                      <option key={rate._id || rate.name} value={rate.name}>
                        {rate.name}{" "}
                        {rate.isUnion && rate.unionName
                          ? `(${rate.unionName})`
                          : rate.isUnion
                            ? "(Union)"
                            : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {showUnion && (
                <div>
                  <label className={labelClass}>Union</label>
                  <select
                    name="union"
                    value={formData.union}
                    onChange={handleChange}
                    disabled={isViewOnly}
                    className={inputClass}
                  >
                    {unionOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {showLevel && (
                <div>
                  <label className={labelClass}>Level</label>
                  <select
                    name="level"
                    value={formData.level || "1"}
                    onChange={handleChange}
                    disabled={isViewOnly}
                    className={inputClass}
                  >
                    {["0", "1", "2", "3", "4"].map((l) => (
                      <option key={l} value={l}>
                        Level {l}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <SectionHeader title="Compensation" />

              <div>
                <label className={labelClass}>Pay Rate</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                    $
                  </span>
                  <input
                    type="number"
                    name="payRate"
                    value={formData.payRate}
                    onChange={handleChange}
                    step="0.01"
                    disabled={isViewOnly}
                    className={`${inputClass} mt-0 pl-7`}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Pay Type</label>
                <div className="relative mt-1">
                  <button
                    type="button"
                    onClick={() =>
                      !isViewOnly && setIsPayTypeOpen(!isPayTypeOpen)
                    }
                    disabled={isViewOnly}
                    className={`w-full rounded-md bg-gray-700 border border-gray-600 text-sm px-3 py-2 text-left flex justify-between items-center disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-orange-400 transition-colors ${
                      isViewOnly
                        ? "cursor-not-allowed"
                        : "hover:border-gray-500"
                    }`}
                  >
                    <span
                      className={
                        formData.payType.length > 0
                          ? "text-gray-100"
                          : "text-gray-500"
                      }
                    >
                      {formData.payType.length > 0
                        ? formData.payType
                            .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
                            .join(", ")
                        : "Select pay types"}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${isPayTypeOpen ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isPayTypeOpen && (
                    <div className="absolute z-20 w-full mt-1 bg-gray-700 rounded-md shadow-xl border border-gray-600 overflow-hidden">
                      {getPayTypeOptions(formData.isTemporary).map((option) => (
                        <label
                          key={option.value}
                          className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-600 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={formData.payType.includes(option.value)}
                            onChange={() => handlePayTypeToggle(option.value)}
                            className="w-4 h-4 text-orange-400 bg-gray-800 border-gray-600 rounded accent-orange-400"
                          />
                          <span className="text-sm text-gray-200">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {showAccount && (
                <div className="col-span-2">
                  <label className={labelClass}>Subcontractor Account</label>
                  <select
                    name="account"
                    value={formData.account || ""}
                    onChange={handleChange}
                    disabled={isViewOnly}
                    className={inputClass}
                  >
                    <option value="">Select an account</option>
                    {accounts.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.companyName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {showHoursPerWeek && (
                <div>
                  <label className={labelClass}>Hours / Week</label>
                  <input
                    type="number"
                    name="hoursPerWeek"
                    value={formData.hoursPerWeek || 40}
                    onChange={handleChange}
                    step="0.5"
                    min="0"
                    disabled={isViewOnly}
                    className={inputClass}
                  />
                </div>
              )}

              {showPieceworker && (
                <div className="col-span-2 bg-gray-700/50 rounded-lg p-4 border border-gray-600/50 space-y-3">
                  <Toggle field="collectsTax" label="Collects Tax" />
                  {formData.collectsTax && (
                    <div>
                      <label className={labelClass}>Tax Rate (%)</label>
                      <input
                        type="number"
                        name="taxRate"
                        value={formData.taxRate || 0}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        max="100"
                        disabled={isViewOnly}
                        className={inputClass}
                      />
                    </div>
                  )}
                </div>
              )}

              <SectionHeader title="Settings" />

              <div className="col-span-2 grid grid-cols-2 gap-3 bg-gray-700/40 rounded-lg p-4 border border-gray-700">
                <Toggle field="active" label="Active" />
                <Toggle field="wsib" label="WSIB" />
                <Toggle field="isTemporary" label="Temporary User" />
                <Toggle field="geoFence" label="GeoFence" />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors border border-gray-600"
              >
                {isViewOnly ? "Close" : "Cancel"}
              </button>
              {!isViewOnly && (
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-md transition-colors shadow-sm"
                >
                  {isEditing ? "Save Changes" : "Add User"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  },
);

export default UserForm;
